import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Star, TrendingUp, TrendingDown, ArrowRight, MapPin, Users, ExternalLink,
  Target, Activity, Trophy, AlertTriangle,
} from "lucide-react";
import api from "../lib/api";
import { Label, EmptyState } from "./common";
import IndexVsMarket from "./IndexVsMarket";

/** Portfolio-level Google benchmarks: KPI row, per-store trend table,
 *  monthly movement chart. Powered by GET /admin/locations/benchmarks/overview
 *  and /admin/locations/benchmarks/trends/{id}.
 */
export default function GoogleBenchmarks() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/admin/locations/benchmarks/overview?months=6")
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, []);

  const linkedRows = useMemo(
    () => (data?.rows || []).filter((r) => r.linked),
    [data],
  );

  useEffect(() => {
    if (!selected && linkedRows.length) setSelected(linkedRows[0].location_id);
  }, [linkedRows, selected]);

  if (err) return null;
  if (!data) return <div className="text-sm text-slate-400">Loading external benchmarks…</div>;

  if (!data.configured) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6" data-testid="google-bench-dormant">
        <div className="flex items-start gap-3">
          <MapPin className="mt-1 h-5 w-5 text-slate-400" />
          <div>
            <Label>External benchmarks</Label>
            <p className="mt-1 text-base font-semibold text-slate-700">Add your Google Maps API key to unlock trends vs area and segment</p>
            <p className="mt-1 text-sm text-slate-500">Once connected, you'll see monthly movements per store, delta vs 2 km area peers, segment averages and leaderboards.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!linkedRows.length) {
    return (
      <EmptyState
        icon={MapPin}
        title="No stores linked to Google yet"
        hint="Open Locations and tap 'Auto-link' or 'Paste link' on each store to populate external benchmarks."
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="google-benchmarks">
      <div>
        <Label>External Benchmarks</Label>
        <h2 className="font-head text-2xl font-bold text-slate-900">Market view · Google</h2>
        <p className="text-sm text-slate-500">
          Monthly movement vs area (within 10 km) and segment (country-wide) peers. {data.portfolio.stores_linked} of {data.portfolio.stores_total} stores linked.
        </p>
      </div>

      <IndexVsMarket variant="full" title="Portfolio: Servantage Index vs the market" />

      <Leaderboard rows={linkedRows} />

      <StoreRankingTable
        rows={linkedRows}
        months={data.months}
        selected={selected}
        onSelect={setSelected}
      />

      {selected && (
        <TrendCharts locationId={selected} />
      )}
    </div>
  );
}

/* ================== Leaderboard ================== */
function Leaderboard({ rows }) {
  const linked = rows.filter((r) => r.google_rating != null);
  if (!linked.length) return null;
  const topRated = [...linked].sort((a, b) => (b.google_rating || 0) - (a.google_rating || 0)).slice(0, 3);
  const needsAttention = [...linked]
    .filter((r) => (r.delta_area ?? 0) < 0)
    .sort((a, b) => (a.delta_area || 0) - (b.delta_area || 0))
    .slice(0, 3);
  const risers = [...linked]
    .filter((r) => (r.mom_google ?? 0) > 0)
    .sort((a, b) => (b.mom_google || 0) - (a.mom_google || 0))
    .slice(0, 3);

  return (
    <div className="grid gap-4 md:grid-cols-3" data-testid="google-bench-leaderboard">
      <Panel icon={Trophy} title="Top-rated stores" accent="#10B981">
        {topRated.map((r) => (
          <Row key={r.location_id} name={r.name} value={fmtStar(r.google_rating)} city={r.city} to={`/app/store?location=${r.location_id}`} />
        ))}
      </Panel>
      <Panel icon={TrendingUp} title="Biggest climbers (MoM)" accent="#4338CA">
        {risers.length === 0 ? (
          <p className="text-xs text-slate-400">No month-over-month movement yet. Trends build with each weekly sync.</p>
        ) : (
          risers.map((r) => (
            <Row key={r.location_id} name={r.name} value={`+${(r.mom_google || 0).toFixed(2)}★`} city={r.city} to={`/app/store?location=${r.location_id}`} />
          ))
        )}
      </Panel>
      <Panel icon={AlertTriangle} title="Below area average" accent="#DC2626">
        {needsAttention.length === 0 ? (
          <p className="text-xs text-slate-400">Every linked store is at or above its area average. 🎉</p>
        ) : (
          needsAttention.map((r) => (
            <Row key={r.location_id} name={r.name} value={`${(r.delta_area || 0).toFixed(2)}★`} city={r.city} to={`/app/store?location=${r.location_id}`} negative />
          ))
        )}
      </Panel>
    </div>
  );
}

function Panel({ icon: Icon, title, accent, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4" style={{ color: accent }} />
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">{title}</p>
      </div>
      <ul className="space-y-2">{children}</ul>
    </div>
  );
}

function Row({ name, value, city, to, negative }) {
  return (
    <li className="flex items-center justify-between gap-2 border-b border-slate-50 pb-2 last:border-0">
      <div className="min-w-0">
        <Link to={to} className="truncate text-sm font-semibold text-slate-800 hover:text-indigo-600">
          {name}
        </Link>
        <p className="truncate text-[11px] text-slate-500">{city || "—"}</p>
      </div>
      <span className={`text-sm font-bold ${negative ? "text-red-600" : "text-emerald-600"}`}>{value}</span>
    </li>
  );
}

/* ================== Store ranking table with sparkline ================== */
function StoreRankingTable({ rows, months, selected, onSelect }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="google-bench-table">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <p className="font-head text-sm font-semibold text-slate-800">Per-store movement</p>
        <p className="hidden text-[11px] text-slate-400 sm:block">Click a row to chart it below</p>
      </div>

      {/* Mobile: stacked cards (<sm). Desktop: table (sm+). */}
      <div className="divide-y divide-slate-100 sm:hidden">
        {rows.map((r) => (
          <button
            key={r.location_id}
            onClick={() => onSelect(r.location_id)}
            className={`block w-full px-4 py-3 text-left transition ${selected === r.location_id ? "bg-amber-50/60" : "active:bg-amber-50/30"}`}
            data-testid={`google-bench-row-mobile-${r.location_id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-800">{r.name}</p>
                <p className="truncate text-[11px] text-slate-500">{r.display_name || r.city}</p>
              </div>
              <span className="font-bold text-amber-700">{fmtStar(r.google_rating)}</span>
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px]">
              <span className="text-slate-500">
                MoM <span className={`font-semibold ${deltaClass(r.mom_google)}`}>{fmtDelta(r.mom_google)}</span>
                {" · "}vs Area <span className={`font-semibold ${deltaClass(r.delta_area)}`}>{fmtDelta(r.delta_area)}</span>
              </span>
              <Sparkline values={r.google_series} w={60} h={20} />
            </div>
          </button>
        ))}
      </div>

      {/* Desktop table with soft inner shadow to hint at horizontal scroll */}
      <div className="hidden overflow-x-auto sm:block [mask-image:linear-gradient(to_right,black_calc(100%-24px),transparent)]">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Store</th>
              <th className="px-3 py-2 text-right">Google</th>
              <th className="px-3 py-2 text-right">MoM</th>
              <th className="px-3 py-2 text-right">vs Area</th>
              <th className="px-3 py-2 text-right">vs Segment</th>
              <th className="px-3 py-2 text-right">Reviews</th>
              <th className="px-3 py-2 text-left">Trend ({months[0].slice(5)}→{months[months.length - 1].slice(5)})</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.location_id}
                onClick={() => onSelect(r.location_id)}
                className={`cursor-pointer border-t border-slate-50 transition hover:bg-amber-50/40 ${selected === r.location_id ? "bg-amber-50/60" : ""}`}
                data-testid={`google-bench-row-${r.location_id}`}
              >
                <td className="px-4 py-3">
                  <p className="font-semibold text-slate-800">{r.name}</p>
                  <p className="truncate text-[11px] text-slate-500">{r.display_name || r.city}</p>
                </td>
                <td className="px-3 py-3 text-right font-bold text-amber-700">{fmtStar(r.google_rating)}</td>
                <td className={`px-3 py-3 text-right font-semibold ${deltaClass(r.mom_google)}`}>{fmtDelta(r.mom_google)}</td>
                <td className={`px-3 py-3 text-right font-semibold ${deltaClass(r.delta_area)}`}>{fmtDelta(r.delta_area)}</td>
                <td className={`px-3 py-3 text-right font-semibold ${deltaClass(r.delta_segment)}`}>{fmtDelta(r.delta_segment)}</td>
                <td className="px-3 py-3 text-right text-slate-600">{r.google_review_count?.toLocaleString() || "—"}</td>
                <td className="px-3 py-3">
                  <Sparkline values={r.google_series} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ================== Sparkline + full trend chart ================== */
function Sparkline({ values, w = 120, h = 28 }) {
  const clean = values.map((v) => (typeof v === "number" ? v : null));
  const nums = clean.filter((v) => v != null);
  if (nums.length < 2) {
    return <span className="text-[11px] text-slate-400">building…</span>;
  }
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const range = max - min || 1;
  const step = w / (clean.length - 1);
  const points = clean.map((v, i) => {
    if (v == null) return null;
    const x = i * step;
    const y = h - ((v - min) / range) * h;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).filter(Boolean).join(" ");
  const last = clean.filter((v) => v != null).slice(-1)[0];
  return (
    <svg width={w} height={h} className="inline-block">
      <polyline fill="none" stroke="#F59E0B" strokeWidth="1.75" points={points} />
      {clean.map((v, i) => v != null ? (
        <circle key={i} cx={i * step} cy={h - ((v - min) / range) * h} r="1.5" fill="#F59E0B" />
      ) : null)}
      <text x={w - 2} y={10} textAnchor="end" className="fill-slate-500" fontSize="9">{last?.toFixed(1)}</text>
    </svg>
  );
}

function TrendCharts({ locationId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/admin/locations/benchmarks/trends/${locationId}?months=12`)
      .then((r) => setData(r.data));
  }, [locationId]);
  if (!data) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="google-bench-trends">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <Label>12-month trend</Label>
          <p className="mt-0.5 font-head text-base font-semibold text-slate-900">
            {data.display_name || data.name}
          </p>
        </div>
        <Link
          to={`/app/store?location=${data.location_id}`}
          className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
        >
          Open store dashboard <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <TrendChart points={data.points} />
      <TrendLegend />
      <ReviewVolumeChart points={data.points} />
    </div>
  );
}

function TrendChart({ points }) {
  const w = 760;
  const h = 240;
  const pad = { l: 36, r: 20, t: 10, b: 24 };
  const inner = { w: w - pad.l - pad.r, h: h - pad.t - pad.b };
  const n = points.length;
  const step = n > 1 ? inner.w / (n - 1) : inner.w;

  // Y: fixed 0-5 scale for star ratings.
  const yFor = (v) => pad.t + inner.h - ((v || 0) / 5) * inner.h;
  const xFor = (i) => pad.l + i * step;

  const series = [
    { key: "google_rating", color: "#F59E0B", label: "Google" },
    { key: "area_avg", color: "#10B981", label: "Area" },
    { key: "segment_avg", color: "#0EA5E9", label: "Segment" },
    { key: "internal_ssi_5", color: "#4338CA", label: "You (SSI)", convert: (v) => (v != null ? v / 20 : null) },
  ];

  return (
    <div className="w-full overflow-x-auto">
      <svg width={w} height={h} className="min-w-[640px]">
        {[1, 2, 3, 4, 5].map((lvl) => (
          <g key={lvl}>
            <line x1={pad.l} x2={w - pad.r} y1={yFor(lvl)} y2={yFor(lvl)} stroke="#F1F5F9" strokeWidth="1" />
            <text x={pad.l - 6} y={yFor(lvl) + 3} textAnchor="end" fontSize="10" className="fill-slate-400">{lvl}</text>
          </g>
        ))}

        {series.map((s) => {
          const vals = points.map((p) => s.convert ? s.convert(p[s.key] ?? p.internal_ssi) : p[s.key]);
          const path = vals.map((v, i) => v == null ? null : `${i === 0 || vals[i - 1] == null ? "M" : "L"} ${xFor(i)} ${yFor(v)}`).filter(Boolean).join(" ");
          return (
            <g key={s.key}>
              <path d={path} fill="none" stroke={s.color} strokeWidth="2" />
              {vals.map((v, i) => v == null ? null : (
                <circle key={i} cx={xFor(i)} cy={yFor(v)} r="2.5" fill={s.color} />
              ))}
            </g>
          );
        })}

        {points.map((p, i) => (
          <text key={i} x={xFor(i)} y={h - 4} textAnchor="middle" fontSize="9" className="fill-slate-400">
            {p.month.slice(5)}
          </text>
        ))}
      </svg>
    </div>
  );
}

function TrendLegend() {
  const items = [
    { label: "Google", color: "#F59E0B" },
    { label: "Area avg", color: "#10B981" },
    { label: "Segment avg", color: "#0EA5E9" },
    { label: "You (SSI /20)", color: "#4338CA" },
  ];
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-600">
      {items.map((i) => (
        <span key={i.label} className="inline-flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: i.color }} />
          {i.label}
        </span>
      ))}
    </div>
  );
}

function ReviewVolumeChart({ points }) {
  const counts = points.map((p) => p.feedback_count || 0);
  const max = Math.max(...counts, 1);
  if (counts.every((c) => c === 0)) return null;
  return (
    <div className="mt-5">
      <Label>In-app feedback volume</Label>
      <div className="mt-1 flex items-end gap-1.5">
        {points.map((p, i) => (
          <div key={i} className="flex flex-1 flex-col items-center">
            <div
              className="w-full rounded-t bg-indigo-400"
              style={{ height: `${(counts[i] / max) * 60 || 2}px` }}
              title={`${counts[i]} responses`}
            />
            <span className="mt-1 text-[9px] text-slate-400">{p.month.slice(5)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ================== helpers ================== */
function fmtStar(v) { return v != null ? `${Number(v).toFixed(1)}★` : "—"; }
function fmtDelta(v) {
  if (v == null) return "—";
  const n = Number(v);
  return `${n >= 0 ? "+" : ""}${n.toFixed(2)}`;
}
function deltaClass(v) {
  if (v == null) return "text-slate-400";
  return v >= 0 ? "text-emerald-600" : "text-red-600";
}
function deltaColor(v) {
  if (v == null) return "#64748B";
  return v >= 0 ? "#10B981" : "#DC2626";
}
